import { z } from 'zod'
import { asObjectType, asEnumType } from '@gqloom/zod'

// Current schema version - increment when making breaking changes
export const CURRENT_SCHEMA_VERSION = 1

// =============================================================================
// Enums
// =============================================================================

export enum AuditAction {
  // Member Events
  MEMBER_CREATED = 'MEMBER_CREATED',
  MEMBER_UPDATED = 'MEMBER_UPDATED',
  MEMBER_DELETED = 'MEMBER_DELETED',
  MEMBER_LOGIN = 'MEMBER_LOGIN',
  MEMBER_LOGOUT = 'MEMBER_LOGOUT',
  MEMBER_CHECKIN = 'MEMBER_CHECKIN',
  MEMBER_CHECKOUT = 'MEMBER_CHECKOUT',
  MEMBER_PROFILE_VIEWED = 'MEMBER_PROFILE_VIEWED',
  MEMBER_CREATED_BULK = 'MEMBER_CREATED_BULK',
  MEMBER_LINKED_TO_SITE_BULK = 'MEMBER_LINKED_TO_SITE_BULK',
  // Client attached to an additional location (corporate-search "Copy to this
  // location" and the legacy corporate-lookup connect flow, OC-636)
  MEMBER_COPIED_TO_LOCATION = 'MEMBER_COPIED_TO_LOCATION',
  // Per-site access block / unblock and irreversible anonymisation (OC-100)
  MEMBER_BLOCKED = 'MEMBER_BLOCKED',
  MEMBER_UNBLOCKED = 'MEMBER_UNBLOCKED',
  MEMBER_ANONYMISED = 'MEMBER_ANONYMISED',
  // OC-1388: the site-scoped assigned staff member changed — set, replaced or cleared, by a
  // staff edit, a CORE sale seeding an empty field, or a merge. One action for all three; the
  // metadata carries the source and the previous value.
  MEMBER_ASSIGNED_STAFF_CHANGED = 'MEMBER_ASSIGNED_STAFF_CHANGED',
  // INT-577: a fob taken from one member and given to another. Recorded against both
  // members, because losing door access is as material to a timeline as gaining it.
  MEMBER_FOB_REASSIGNED = 'MEMBER_FOB_REASSIGNED',

  // Session/Booking Events
  SESSION_BOOKED = 'SESSION_BOOKED',
  SESSION_CANCELLED = 'SESSION_CANCELLED',
  SESSION_ATTENDED = 'SESSION_ATTENDED',
  SESSION_NO_SHOW = 'SESSION_NO_SHOW',
  SESSION_WAITLISTED = 'SESSION_WAITLISTED',
  SESSION_CREATED = 'SESSION_CREATED',
  SESSION_UPDATED = 'SESSION_UPDATED',
  SESSION_DELETED = 'SESSION_DELETED',
  BOOKING_CREDIT_PACKAGE_CHANGED = 'BOOKING_CREDIT_PACKAGE_CHANGED',
  BOOKING_PAYMENT_TYPE_CHANGED = 'BOOKING_PAYMENT_TYPE_CHANGED',
  BOOKING_CREDIT_COST_ADJUSTED = 'BOOKING_CREDIT_COST_ADJUSTED',

  // Payment Events
  PAYMENT_PROCESSED = 'PAYMENT_PROCESSED',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  PAYMENT_REFUNDED = 'PAYMENT_REFUNDED',
  PAYMENT_GATEWAY_FEES_APPLIED = 'PAYMENT_GATEWAY_FEES_APPLIED', // CORE-1313: Ezypay load/failed-payment fees recorded on settlement
  PAYMENT_METHOD_ADDED = 'PAYMENT_METHOD_ADDED',
  PAYMENT_METHOD_REMOVED = 'PAYMENT_METHOD_REMOVED',
  // OC-664: bank/EFT decline failover to a backup card. SKIPPED and EXHAUSTED are
  // recorded as deliberately as the successful path — "why did this member's invoice
  // not fail over?" has to be answerable from the audit trail rather than from logs.
  PAYMENT_FALLBACK_SCHEDULED = 'PAYMENT_FALLBACK_SCHEDULED',
  PAYMENT_FALLBACK_ATTEMPTED = 'PAYMENT_FALLBACK_ATTEMPTED',
  PAYMENT_FALLBACK_RECOVERED = 'PAYMENT_FALLBACK_RECOVERED',
  PAYMENT_FALLBACK_EXHAUSTED = 'PAYMENT_FALLBACK_EXHAUSTED',
  PAYMENT_FALLBACK_SKIPPED = 'PAYMENT_FALLBACK_SKIPPED',
  PAYMENT_METHOD_BACKUP_SET = 'PAYMENT_METHOD_BACKUP_SET',
  PAYMENT_METHOD_BACKUP_CLEARED = 'PAYMENT_METHOD_BACKUP_CLEARED',
  // OC-128: cash drawer money-custody events (open with counted float / close with variance snapshot)
  CASH_DRAWER_OPENED = 'CASH_DRAWER_OPENED',
  CASH_DRAWER_CLOSED = 'CASH_DRAWER_CLOSED',
  // CORE-1322 / INT-67: Accounting-code library + template assignment.
  // Enum values are gateway-neutral (DD-011); user-facing audit-table labels read "Ezypay account code …".
  ACCOUNT_CODE_ACTIVATED = 'ACCOUNT_CODE_ACTIVATED',
  ACCOUNT_CODE_ASSIGNED = 'ACCOUNT_CODE_ASSIGNED',
  ACCOUNT_CODE_CREATED = 'ACCOUNT_CODE_CREATED',
  ACCOUNT_CODE_DEACTIVATED = 'ACCOUNT_CODE_DEACTIVATED',
  ACCOUNT_CODE_UNASSIGNED = 'ACCOUNT_CODE_UNASSIGNED',
  ACCOUNT_CODE_UPDATED = 'ACCOUNT_CODE_UPDATED',
  INVOICE_CREATED = 'INVOICE_CREATED',
  INVOICE_PAID = 'INVOICE_PAID',
  INVOICE_VOIDED = 'INVOICE_VOIDED',
  INVOICE_CANCELLED = 'INVOICE_CANCELLED',
  INVOICE_REINSTATED = 'INVOICE_REINSTATED',
  INVOICES_IMPORTED = 'INVOICES_IMPORTED',
  SALE_OUTSIDE_AVAILABILITY_OVERRIDE = 'SALE_OUTSIDE_AVAILABILITY_OVERRIDE',
  SALE_OUTSIDE_DYNAMIC_DISPLAY_OVERRIDE = 'SALE_OUTSIDE_DYNAMIC_DISPLAY_OVERRIDE',

  // Membership Events
  MEMBERSHIP_CREATED = 'MEMBERSHIP_CREATED',
  MEMBERSHIP_UPDATED = 'MEMBERSHIP_UPDATED',
  MEMBERSHIP_CANCELLED = 'MEMBERSHIP_CANCELLED',
  MEMBERSHIP_SUSPENDED = 'MEMBERSHIP_SUSPENDED',
  MEMBERSHIP_RESUMED = 'MEMBERSHIP_RESUMED',
  MEMBERSHIP_RENEWED = 'MEMBERSHIP_RENEWED',

  // OC-8 Shared Credits — share lifecycle on a credit-based Service (package)
  PACKAGE_SHARED = 'PACKAGE_SHARED',
  PACKAGE_SHARE_REVOKED = 'PACKAGE_SHARE_REVOKED',
  SERVICE_CREDITS_ADJUSTED = 'SERVICE_CREDITS_ADJUSTED',

  FAMILY_CREATED = 'FAMILY_CREATED',
  FAMILY_MEMBER_INVITED = 'FAMILY_MEMBER_INVITED',
  FAMILY_INVITE_LINK_CREATED = 'FAMILY_INVITE_LINK_CREATED',
  FAMILY_MEMBER_JOINED = 'FAMILY_MEMBER_JOINED',
  FAMILY_MEMBER_DECLINED = 'FAMILY_MEMBER_DECLINED',
  FAMILY_MEMBER_ADDED = 'FAMILY_MEMBER_ADDED',
  FAMILY_MEMBER_REMOVED = 'FAMILY_MEMBER_REMOVED',
  FAMILY_MEMBER_LEFT = 'FAMILY_MEMBER_LEFT',
  FAMILY_ROLE_CHANGED = 'FAMILY_ROLE_CHANGED',
  FAMILY_PERMISSION_CHANGED = 'FAMILY_PERMISSION_CHANGED',
  FAMILY_DISSOLVED = 'FAMILY_DISSOLVED',
  FAMILY_PACKAGE_ALLOCATED = 'FAMILY_PACKAGE_ALLOCATED',
  FAMILY_ALLOCATION_UPDATED = 'FAMILY_ALLOCATION_UPDATED',
  FAMILY_ALLOCATION_REVOKED = 'FAMILY_ALLOCATION_REVOKED',
  FAMILY_DUPLICATE_SERVICE_FLAGGED = 'FAMILY_DUPLICATE_SERVICE_FLAGGED',

  // Staff Events
  STAFF_LOGIN = 'STAFF_LOGIN',
  STAFF_LOGOUT = 'STAFF_LOGOUT',
  STAFF_CREATED = 'STAFF_CREATED',
  STAFF_UPDATED = 'STAFF_UPDATED',
  STAFF_DELETED = 'STAFF_DELETED',
  STAFF_ROLE_CHANGED = 'STAFF_ROLE_CHANGED',
  STAFF_CLOCK_IN = 'STAFF_CLOCK_IN',
  STAFF_CLOCK_OUT = 'STAFF_CLOCK_OUT',
  STAFF_TIME_CLOCK_MANUAL_ENTRY = 'STAFF_TIME_CLOCK_MANUAL_ENTRY',
  ACCESS_POLICY_CREATED = 'ACCESS_POLICY_CREATED',
  ACCESS_POLICY_UPDATED = 'ACCESS_POLICY_UPDATED',
  ACCESS_POLICY_DELETED = 'ACCESS_POLICY_DELETED',
  STAFF_ALLOWED_NETWORKS_CHANGED = 'STAFF_ALLOWED_NETWORKS_CHANGED',
  STAFF_SESSIONS_REVOKED = 'STAFF_SESSIONS_REVOKED',
  ALLOWED_NETWORK_CREATED = 'ALLOWED_NETWORK_CREATED',
  ALLOWED_NETWORK_UPDATED = 'ALLOWED_NETWORK_UPDATED',
  ALLOWED_NETWORK_DELETED = 'ALLOWED_NETWORK_DELETED',
  STAFF_SESSION_POLICY_UPDATED = 'STAFF_SESSION_POLICY_UPDATED',

  // Admin Events
  ADMIN_USER_LOGIN = 'ADMIN_USER_LOGIN',
  ADMIN_USER_LOGOUT = 'ADMIN_USER_LOGOUT',
  ADMIN_USER_CREATED = 'ADMIN_USER_CREATED',
  ADMIN_USER_UPDATED = 'ADMIN_USER_UPDATED',
  ADMIN_USER_DELETED = 'ADMIN_USER_DELETED',

  // Corporate/Site Events
  CORPORATE_CREATED = 'CORPORATE_CREATED',
  CORPORATE_UPDATED = 'CORPORATE_UPDATED',
  SITE_CREATED = 'SITE_CREATED',
  SITE_UPDATED = 'SITE_UPDATED',
  SITE_DELETED = 'SITE_DELETED',
  SITE_V2_STATUS_CHANGED = 'SITE_V2_STATUS_CHANGED',
  SITE_DUPLICATED = 'SITE_DUPLICATED',
  EZYPAY_WEBHOOK_SWITCHED = 'EZYPAY_WEBHOOK_SWITCHED',
  SITE_INDEX_BACKFILLED = 'SITE_INDEX_BACKFILLED',
  SITE_ALERT_POSTED = 'SITE_ALERT_POSTED',
  SITE_ALERT_UPDATED = 'SITE_ALERT_UPDATED',
  SITE_ALERT_ENDED = 'SITE_ALERT_ENDED',
  SITE_ALERT_DELETED = 'SITE_ALERT_DELETED',

  // Door/Access Events
  DOOR_ACCESS_GRANTED = 'DOOR_ACCESS_GRANTED',
  DOOR_ACCESS_DENIED = 'DOOR_ACCESS_DENIED',
  DOOR_UNLOCKED = 'DOOR_UNLOCKED',
  DOOR_UNLOCK_FAILED = 'DOOR_UNLOCK_FAILED',
  DOOR_LOCKED = 'DOOR_LOCKED',
  ACCESS_POINT_MOVED = 'ACCESS_POINT_MOVED',
  ACCESS_POINT_DELETED = 'ACCESS_POINT_DELETED',
  ACCESS_POINT_CREATED = 'ACCESS_POINT_CREATED',
  ACCESS_POINT_UPDATED = 'ACCESS_POINT_UPDATED',
  ACCESS_POINT_QR_GENERATED = 'ACCESS_POINT_QR_GENERATED',
  ACCESS_ZONE_CREATED = 'ACCESS_ZONE_CREATED',
  ACCESS_ZONE_UPDATED = 'ACCESS_ZONE_UPDATED',
  ACCESS_ZONE_DELETED = 'ACCESS_ZONE_DELETED',

  // System Events
  SYSTEM_CONFIG_CHANGED = 'SYSTEM_CONFIG_CHANGED',
  DATA_EXPORT = 'DATA_EXPORT',
  DATA_IMPORT = 'DATA_IMPORT',
  GDPR_REQUEST = 'GDPR_REQUEST',
  DATA_DELETION = 'DATA_DELETION',

  // Settings Events
  SETTING_UPDATED = 'SETTING_UPDATED',
  SITE_SETTING_UPDATED = 'SITE_SETTING_UPDATED',
  BRAND_SETTING_UPDATED = 'BRAND_SETTING_UPDATED',
  // MEM-301 — a location's fob credential format changed and every stored fob was
  // recomputed from its raw serial. Carries the old and new config plus the batch
  // counts, so a door code that changed shape stays traceable to the change that moved it.
  SITE_FOB_FORMAT_CHANGED = 'SITE_FOB_FORMAT_CHANGED',

  // Page/Navigation Events
  PAGE_VIEWED = 'PAGE_VIEWED',
  FEATURE_ACCESSED = 'FEATURE_ACCESSED',

  // Knowledge Hub Events
  KNOWLEDGE_ARTICLE_CREATED = 'KNOWLEDGE_ARTICLE_CREATED',
  KNOWLEDGE_ARTICLE_UPDATED = 'KNOWLEDGE_ARTICLE_UPDATED',
  KNOWLEDGE_ARTICLE_PUBLISHED = 'KNOWLEDGE_ARTICLE_PUBLISHED',
  KNOWLEDGE_ARTICLE_ARCHIVED = 'KNOWLEDGE_ARTICLE_ARCHIVED',
  KNOWLEDGE_ARTICLE_DELETED = 'KNOWLEDGE_ARTICLE_DELETED',
  KNOWLEDGE_ARTICLE_RESTORED = 'KNOWLEDGE_ARTICLE_RESTORED',
  KNOWLEDGE_ARTICLE_TRANSLATION_ADDED = 'KNOWLEDGE_ARTICLE_TRANSLATION_ADDED',

  // Knowledge Category Events
  KNOWLEDGE_CATEGORY_CREATED = 'KNOWLEDGE_CATEGORY_CREATED',
  KNOWLEDGE_CATEGORY_UPDATED = 'KNOWLEDGE_CATEGORY_UPDATED',
  KNOWLEDGE_CATEGORY_DELETED = 'KNOWLEDGE_CATEGORY_DELETED',
  KNOWLEDGE_CATEGORY_REORDERED = 'KNOWLEDGE_CATEGORY_REORDERED',

  // AI Assistant Events
  AI_ARTICLE_GENERATED = 'AI_ARTICLE_GENERATED',
  AI_CONVERSATION_STARTED = 'AI_CONVERSATION_STARTED',
  AI_CONVERSATION_ENDED = 'AI_CONVERSATION_ENDED',
  AI_INTENT_DETECTED = 'AI_INTENT_DETECTED',
  AI_RESPONSE_GENERATED = 'AI_RESPONSE_GENERATED',
  AI_FEEDBACK_PROVIDED = 'AI_FEEDBACK_PROVIDED',
  AI_SUPPORT_TICKET_CREATED = 'AI_SUPPORT_TICKET_CREATED',
  AI_KNOWLEDGE_SEARCH = 'AI_KNOWLEDGE_SEARCH',

  // Support Ticket Events
  SUPPORT_TICKET_SUBMITTED = 'SUPPORT_TICKET_SUBMITTED',

  // Knowledge Media Events
  KNOWLEDGE_ARTICLE_VIDEO_UPLOADED = 'KNOWLEDGE_ARTICLE_VIDEO_UPLOADED',
  KNOWLEDGE_ARTICLE_IMAGE_UPLOADED = 'KNOWLEDGE_ARTICLE_IMAGE_UPLOADED',

  // Integration Sync Events
  INTEGRATION_SYNC_STARTED = 'INTEGRATION_SYNC_STARTED',
  INTEGRATION_SYNC_COMPLETED = 'INTEGRATION_SYNC_COMPLETED',
  INTEGRATION_SYNC_FAILED = 'INTEGRATION_SYNC_FAILED',
  INTEGRATION_CLIENT_SYNCED = 'INTEGRATION_CLIENT_SYNCED',
  INTEGRATION_CLIENT_SYNC_FAILED = 'INTEGRATION_CLIENT_SYNC_FAILED',
  INTEGRATION_TOKEN_REFRESHED = 'INTEGRATION_TOKEN_REFRESHED',
  INTEGRATION_TOKEN_REFRESH_FAILED = 'INTEGRATION_TOKEN_REFRESH_FAILED',
  // Integration configuration changed (e.g. field-mapping edits, visibility)
  INTEGRATION_CONFIG_UPDATED = 'INTEGRATION_CONFIG_UPDATED',
  INTEGRATION_CONNECTED = 'INTEGRATION_CONNECTED',
  INTEGRATION_DISCONNECTED = 'INTEGRATION_DISCONNECTED',

  // Grow Corporate Settings Events (Admin UI)
  GROW_CORPORATE_SETTINGS_UPDATED = 'GROW_CORPORATE_SETTINGS_UPDATED',

  // Grow HQ Events (Admin UI)
  GROW_HQ_CREATED = 'GROW_HQ_CREATED',
  GROW_HQ_UPDATED = 'GROW_HQ_UPDATED',
  GROW_HQ_DELETED = 'GROW_HQ_DELETED',
  GROW_HQ_AUTHENTICATION_SET = 'GROW_HQ_AUTHENTICATION_SET',
  GROW_HQ_AUTHENTICATION_CLEARED = 'GROW_HQ_AUTHENTICATION_CLEARED',
  GROW_HQ_SYNC_TRIGGERED = 'GROW_HQ_SYNC_TRIGGERED',

  // Grow Site Events (Admin UI)
  GROW_SITE_AUTHENTICATION_SET = 'GROW_SITE_AUTHENTICATION_SET',
  GROW_SITE_AUTHENTICATION_CLEARED = 'GROW_SITE_AUTHENTICATION_CLEARED',
  GROW_SITE_LINKED_TO_HQ = 'GROW_SITE_LINKED_TO_HQ',
  GROW_SITE_UNLINKED_FROM_HQ = 'GROW_SITE_UNLINKED_FROM_HQ',
  GROW_SITE_LOCATION_CREATED = 'GROW_SITE_LOCATION_CREATED',
  GROW_SITE_SYNC_TRIGGERED = 'GROW_SITE_SYNC_TRIGGERED',
  GROW_SITE_SETTINGS_UPDATED = 'GROW_SITE_SETTINGS_UPDATED',

  // Grow Custom Field Events (System — GHL field sync)
  GROW_CUSTOM_FIELD_SYNCED = 'GROW_CUSTOM_FIELD_SYNCED',
  GROW_CUSTOM_FIELDS_CACHE_REFRESHED = 'GROW_CUSTOM_FIELDS_CACHE_REFRESHED',

  // Grow Token Events (System)
  GROW_TOKEN_REFRESHED = 'GROW_TOKEN_REFRESHED',
  GROW_TOKEN_REFRESH_FAILED = 'GROW_TOKEN_REFRESH_FAILED',

  // Webhook Events
  WEBHOOK_CREATED = 'WEBHOOK_CREATED',
  WEBHOOK_UPDATED = 'WEBHOOK_UPDATED',
  WEBHOOK_DELETED = 'WEBHOOK_DELETED',
  WEBHOOK_ENABLED = 'WEBHOOK_ENABLED',
  WEBHOOK_DISABLED = 'WEBHOOK_DISABLED',
  WEBHOOK_DISABLED_AUTO = 'WEBHOOK_DISABLED_AUTO',
  WEBHOOK_SECRET_ROTATED = 'WEBHOOK_SECRET_ROTATED',
  INTEGRATION_WEBHOOK_DELIVERY_SUCCEEDED = 'INTEGRATION_WEBHOOK_DELIVERY_SUCCEEDED',
  INTEGRATION_WEBHOOK_DELIVERY_FAILED = 'INTEGRATION_WEBHOOK_DELIVERY_FAILED',

  // Public-API Credential Events (INT-705) — issuing a key mints a principal
  // that can read brand data, so every lifecycle step is a security event.
  API_CREDENTIAL_CREATED = 'API_CREDENTIAL_CREATED',
  API_CREDENTIAL_RENAMED = 'API_CREDENTIAL_RENAMED',
  API_CREDENTIAL_ROTATED = 'API_CREDENTIAL_ROTATED',
  API_CREDENTIAL_SCOPES_UPDATED = 'API_CREDENTIAL_SCOPES_UPDATED',
  API_CREDENTIAL_REVOKED = 'API_CREDENTIAL_REVOKED',

  // Zapier Connection Events
  ZAPIER_LOCATION_CONNECTED = 'ZAPIER_LOCATION_CONNECTED',
  ZAPIER_LOCATION_DISCONNECTED = 'ZAPIER_LOCATION_DISCONNECTED',
  ZAPIER_SUBSCRIPTION_CREATED = 'ZAPIER_SUBSCRIPTION_CREATED',
  ZAPIER_SUBSCRIPTION_REMOVED = 'ZAPIER_SUBSCRIPTION_REMOVED',

  // Fitness Passport Events
  //
  // `linkedByStaffUserId` on the link row records who linked a member, but it is a single
  // mutable field — it cannot say who REMOVED a link, when a token was rotated, or who triggered
  // a billable replay. Those are the actions an operator has to be able to account for when
  // reconciling against Fitness Passport's statement.
  FITNESS_PASSPORT_MEMBER_LINKED = 'FITNESS_PASSPORT_MEMBER_LINKED',
  FITNESS_PASSPORT_MEMBER_UNLINKED = 'FITNESS_PASSPORT_MEMBER_UNLINKED',
  FITNESS_PASSPORT_MEMBER_REFRESHED = 'FITNESS_PASSPORT_MEMBER_REFRESHED',
  FITNESS_PASSPORT_TOKENS_UPDATED = 'FITNESS_PASSPORT_TOKENS_UPDATED',
  FITNESS_PASSPORT_CONNECTION_TESTED = 'FITNESS_PASSPORT_CONNECTION_TESTED',
  FITNESS_PASSPORT_VISITS_REPLAYED = 'FITNESS_PASSPORT_VISITS_REPLAYED',

  // Referral Events
  REFERRAL_RECORDED = 'REFERRAL_RECORDED',
  REFERRAL_BONUS_AWARDED = 'REFERRAL_BONUS_AWARDED',
  REFERRAL_DECLINED = 'REFERRAL_DECLINED',

  // Additional backend events
  INVOICE_UPDATED = 'INVOICE_UPDATED',
  MEMBER_MERGED = 'MEMBER_MERGED',
  IMPERSONATION_STARTED = 'IMPERSONATION_STARTED',
  IMPERSONATION_ENDED = 'IMPERSONATION_ENDED',

  // Communication events
  EMAIL_SENT = 'EMAIL_SENT',
  EMAIL_DELIVERED = 'EMAIL_DELIVERED',
  EMAIL_OPENED = 'EMAIL_OPENED',
  EMAIL_BOUNCED = 'EMAIL_BOUNCED',
  EMAIL_COMPLAINED = 'EMAIL_COMPLAINED',
  EMAIL_CLICKED = 'EMAIL_CLICKED',
  EMAIL_FAILED = 'EMAIL_FAILED',
  EMAIL_SUPPRESSED = 'EMAIL_SUPPRESSED',
  SMS_SENT = 'SMS_SENT',
  SMS_DELIVERED = 'SMS_DELIVERED',
  PUSH_NOTIFICATION_SENT = 'PUSH_NOTIFICATION_SENT',
  PUSH_NOTIFICATION_DELIVERED = 'PUSH_NOTIFICATION_DELIVERED',

  // Notes events
  NOTE_CREATED = 'NOTE_CREATED',
  NOTE_UPDATED = 'NOTE_UPDATED',

  // Ecommerce events
  ECOMMERCE_PURCHASE = 'ECOMMERCE_PURCHASE',

  // Dispute Events
  DISPUTE_CREATED = 'DISPUTE_CREATED',
  DISPUTE_WON = 'DISPUTE_WON',
  DISPUTE_LOST = 'DISPUTE_LOST',

  // Content Suite — Authoring Events (MEM-413)
  // Staff-side content lifecycle. Distinct from the member consumption events below
  // (CONTENT_VIEWED / _COMPLETED / _FAVORITED / _DOWNLOADED), which record what a
  // member did with published content rather than who changed it.
  CONTENT_CREATED = 'CONTENT_CREATED',
  CONTENT_UPDATED = 'CONTENT_UPDATED',
  CONTENT_PUBLISHED = 'CONTENT_PUBLISHED',
  CONTENT_UNPUBLISHED = 'CONTENT_UNPUBLISHED',
  CONTENT_ARCHIVED = 'CONTENT_ARCHIVED',
  // Soft delete (recoverable — sets deletedAt) vs hard delete (row is gone).
  // Deliberately separate actions: the Brands audit column and its filters must
  // distinguish a reversible delete from an irreversible one without anyone
  // having to read metadataJson.
  CONTENT_DELETED = 'CONTENT_DELETED',
  CONTENT_PERMANENTLY_DELETED = 'CONTENT_PERMANENTLY_DELETED',
  CONTENT_DUPLICATED = 'CONTENT_DUPLICATED',
  CONTENT_REORDERED = 'CONTENT_REORDERED',
  CONTENT_VERSION_RESTORED = 'CONTENT_VERSION_RESTORED',
  CONTENT_SCHEDULED_PUBLISH_SKIPPED = 'CONTENT_SCHEDULED_PUBLISH_SKIPPED',

  // Content Suite Events
  CONTENT_VIEWED = 'CONTENT_VIEWED',
  CONTENT_COMPLETED = 'CONTENT_COMPLETED',
  CONTENT_FAVORITED = 'CONTENT_FAVORITED',
  CONTENT_UNFAVORITED = 'CONTENT_UNFAVORITED',
  VIDEO_SESSION_STARTED = 'VIDEO_SESSION_STARTED',
  VIDEO_COMPLETED = 'VIDEO_COMPLETED',
  CHALLENGE_ENROLLED = 'CHALLENGE_ENROLLED',
  CHALLENGE_WITHDRAWN = 'CHALLENGE_WITHDRAWN',
  CHALLENGE_RESUMED = 'CHALLENGE_RESUMED',
  CHALLENGE_COMPLETED = 'CHALLENGE_COMPLETED',
  CHALLENGE_EXPIRED = 'CHALLENGE_EXPIRED',
  CHALLENGE_GOAL_SUBMITTED = 'CHALLENGE_GOAL_SUBMITTED',
  CHALLENGE_ASSESSMENT_SUBMITTED = 'CHALLENGE_ASSESSMENT_SUBMITTED',
  CHALLENGE_ENROLLMENT_ARCHIVED = 'CHALLENGE_ENROLLMENT_ARCHIVED',
  METRIC_ENTRY_CREATED = 'METRIC_ENTRY_CREATED',
  METRIC_ENTRY_DELETED = 'METRIC_ENTRY_DELETED',
  METRIC_PERSONAL_BEST = 'METRIC_PERSONAL_BEST',
  AWARD_EARNED = 'AWARD_EARNED',
  STREAK_STARTED = 'STREAK_STARTED',
  STREAK_BROKEN = 'STREAK_BROKEN',
  STREAK_ADJUSTED = 'STREAK_ADJUSTED',
  STREAK_SETTINGS_UPDATED = 'STREAK_SETTINGS_UPDATED',
  MILESTONE_REACHED = 'MILESTONE_REACHED',
  LEADERBOARD_MATERIALIZED = 'LEADERBOARD_MATERIALIZED',
  LEADERBOARD_RANK_CHANGED = 'LEADERBOARD_RANK_CHANGED',

  // Content Suite — Carousel Group Events
  CAROUSEL_GROUP_CREATED = 'CAROUSEL_GROUP_CREATED',
  CAROUSEL_GROUP_UPDATED = 'CAROUSEL_GROUP_UPDATED',
  CAROUSEL_GROUP_DELETED = 'CAROUSEL_GROUP_DELETED',

  // Content Suite — Goal Events
  GOAL_CREATED = 'GOAL_CREATED',
  GOAL_UPDATED = 'GOAL_UPDATED',
  GOAL_DELETED = 'GOAL_DELETED',

  // Content Suite — Offline Download Events
  CONTENT_DOWNLOADED = 'CONTENT_DOWNLOADED',
  CONTENT_DOWNLOAD_BATCH_SYNCED = 'CONTENT_DOWNLOAD_BATCH_SYNCED',
  CONTENT_DOWNLOAD_ACCESS_CHECK = 'CONTENT_DOWNLOAD_ACCESS_CHECK',
  CONTENT_WATCH_LATER_ADDED = 'CONTENT_WATCH_LATER_ADDED',
  CONTENT_WATCH_LATER_REMOVED = 'CONTENT_WATCH_LATER_REMOVED',

  // Content Suite — Share Events
  SHARE_GENERATED = 'SHARE_GENERATED',
  SHARE_COMPLETED = 'SHARE_COMPLETED',

  // Demo Events
  DEMO_SIGNUP = 'DEMO_SIGNUP',
  DEMO_EMAIL_VERIFIED = 'DEMO_EMAIL_VERIFIED',
  DEMO_CORPORATE_CREATED = 'DEMO_CORPORATE_CREATED',
  DEMO_SEEDING_STARTED = 'DEMO_SEEDING_STARTED',
  DEMO_SEEDING_COMPLETED = 'DEMO_SEEDING_COMPLETED',
  DEMO_SEEDING_FAILED = 'DEMO_SEEDING_FAILED',
  DEMO_READY = 'DEMO_READY',
  DEMO_CLEANUP = 'DEMO_CLEANUP',
  TRIAL_ACCESS_PROVISIONED = 'TRIAL_ACCESS_PROVISIONED',

  // Sandbox (INT-18) — audited at trigger time only
  SANDBOX_CREATE_TRIGGERED = 'SANDBOX_CREATE_TRIGGERED',
  SANDBOX_RESYNC_TRIGGERED = 'SANDBOX_RESYNC_TRIGGERED',
  SANDBOX_DELETE_TRIGGERED = 'SANDBOX_DELETE_TRIGGERED',
  SANDBOX_SITE_INDEX_EVICTED = 'SANDBOX_SITE_INDEX_EVICTED',

  // Trial Lifecycle Events
  TRIAL_STARTED = 'TRIAL_STARTED',
  TRIAL_EXTENDED = 'TRIAL_EXTENDED',
  TRIAL_CONVERTED = 'TRIAL_CONVERTED',
  TRIAL_EXPIRED = 'TRIAL_EXPIRED',
  TRIAL_CLOSED = 'TRIAL_CLOSED',
  TRIAL_REOPENED = 'TRIAL_REOPENED',

  // Staff Invitation Events
  STAFF_INVITATION_SENT = 'STAFF_INVITATION_SENT',
  STAFF_INVITATION_REVOKED = 'STAFF_INVITATION_REVOKED',
  STAFF_INVITATION_RESENT = 'STAFF_INVITATION_RESENT',
  STAFF_INVITATION_ACCEPTED = 'STAFF_INVITATION_ACCEPTED',

  // Onboarding Events
  ONBOARDING_STEP_COMPLETED = 'ONBOARDING_STEP_COMPLETED',
  ONBOARDING_STEP_VIEWED = 'ONBOARDING_STEP_VIEWED',

  // Authentication / Communication Events
  PASSWORD_RESET_REQUESTED = 'PASSWORD_RESET_REQUESTED',
  MAGIC_LINK_SENT = 'MAGIC_LINK_SENT',

  // Member Agreement (e-signing) Events
  AGREEMENT_SENT = 'AGREEMENT_SENT',
  AGREEMENT_VIEWED = 'AGREEMENT_VIEWED',
  AGREEMENT_SIGNED = 'AGREEMENT_SIGNED',
  AGREEMENT_VOIDED = 'AGREEMENT_VOIDED',

  // Equipment policy compliance (OC-1933)
  SITE_POLICY_REVISION_PUBLISHED = 'SITE_POLICY_REVISION_PUBLISHED',
  SITE_POLICY_REVISION_SCHEDULED = 'SITE_POLICY_REVISION_SCHEDULED',
  SITE_POLICY_REVISION_CANCELLED = 'SITE_POLICY_REVISION_CANCELLED',
  ELECTRONIC_CONSENT_WITHDRAWN = 'ELECTRONIC_CONSENT_WITHDRAWN',
  GUARDIAN_PERMISSION_WITHDRAWN = 'GUARDIAN_PERMISSION_WITHDRAWN',
  GUARDIAN_AUTHORITY_REVOKED = 'GUARDIAN_AUTHORITY_REVOKED',
  POLICY_ACCEPTANCE_VOIDED = 'POLICY_ACCEPTANCE_VOIDED',
  CLIENT_AGE_VERIFIED = 'CLIENT_AGE_VERIFIED',
  CLIENT_DOB_CORRECTED = 'CLIENT_DOB_CORRECTED',
  PENDING_ITEMS_HANDOFF_CREATED = 'PENDING_ITEMS_HANDOFF_CREATED',
  PENDING_ITEMS_HANDOFF_CLOSED = 'PENDING_ITEMS_HANDOFF_CLOSED',
  PENDING_ITEMS_HANDOFF_COMPLETED = 'PENDING_ITEMS_HANDOFF_COMPLETED',
  PENDING_ITEMS_IDENTITY_FAILED = 'PENDING_ITEMS_IDENTITY_FAILED',
  ELECTRONIC_CONSENT_GIVEN = 'ELECTRONIC_CONSENT_GIVEN',
  POLICY_ACCEPTED = 'POLICY_ACCEPTED',
  POLICY_PAPER_RECORDED = 'POLICY_PAPER_RECORDED',
  POLICY_EVIDENCE_IMPORTED = 'POLICY_EVIDENCE_IMPORTED',
  POLICY_IMPORT_VOIDED = 'POLICY_IMPORT_VOIDED',
  STAFF_APPROVAL_PIN_SET = 'STAFF_APPROVAL_PIN_SET',
  SITE_POLICY_REVISION_ACTIVATED = 'SITE_POLICY_REVISION_ACTIVATED',
  POLICY_RENEWAL_REMINDER_SKIPPED = 'POLICY_RENEWAL_REMINDER_SKIPPED',
  PENDING_ITEMS_AUTO_SEND_SKIPPED = 'PENDING_ITEMS_AUTO_SEND_SKIPPED',
  SALE_HANDOFF_CREATED = 'SALE_HANDOFF_CREATED',
  SALE_HANDOFF_RESENT = 'SALE_HANDOFF_RESENT',
  SALE_HANDOFF_CLOSED = 'SALE_HANDOFF_CLOSED',
  SALE_HANDOFF_OPENED = 'SALE_HANDOFF_OPENED',
  SALE_HANDOFF_IDENTITY_FAILED = 'SALE_HANDOFF_IDENTITY_FAILED',
  SALE_HANDOFF_AGREEMENT_SIGNED = 'SALE_HANDOFF_AGREEMENT_SIGNED',
  SALE_HANDOFF_PROMO_APPLIED = 'SALE_HANDOFF_PROMO_APPLIED',
  SALE_HANDOFF_PROMO_REMOVED = 'SALE_HANDOFF_PROMO_REMOVED',
  SALE_HANDOFF_GIFT_CARD_REDEEMED = 'SALE_HANDOFF_GIFT_CARD_REDEEMED',
  SALE_HANDOFF_CREDIT_SET = 'SALE_HANDOFF_CREDIT_SET',
  SALE_HANDOFF_PAID = 'SALE_HANDOFF_PAID',
  SALE_HANDOFF_PAYMENT_FAILED = 'SALE_HANDOFF_PAYMENT_FAILED',
  SALE_HANDOFF_CARD_SAVED = 'SALE_HANDOFF_CARD_SAVED',
  POLICY_BULK_SEND_STARTED = 'POLICY_BULK_SEND_STARTED',
  POLICY_BULK_SEND_CANCELLED = 'POLICY_BULK_SEND_CANCELLED',
  POLICY_BULK_SEND_COMPLETED = 'POLICY_BULK_SEND_COMPLETED',
  POLICY_EVIDENCE_EXPORT_REQUESTED = 'POLICY_EVIDENCE_EXPORT_REQUESTED',
  POLICY_EVIDENCE_EXPORTED = 'POLICY_EVIDENCE_EXPORTED',
  POLICY_EVIDENCE_EXPORT_DOWNLOADED = 'POLICY_EVIDENCE_EXPORT_DOWNLOADED',
  POLICY_EVIDENCE_EXPORT_REVOKED = 'POLICY_EVIDENCE_EXPORT_REVOKED',
  POLICY_EVIDENCE_PURGED = 'POLICY_EVIDENCE_PURGED',
  POLICY_COMPLIANCE_SETTINGS_UPDATED = 'POLICY_COMPLIANCE_SETTINGS_UPDATED',
  EQUIPMENT_JURISDICTION_RULE_SAVED = 'EQUIPMENT_JURISDICTION_RULE_SAVED',
  EQUIPMENT_JURISDICTION_RULE_RETIRED = 'EQUIPMENT_JURISDICTION_RULE_RETIRED',

  // Member Movement Events (OC-554)
  MEMBERSHIP_TRANSFERRED = 'MEMBERSHIP_TRANSFERRED',
  MEMBERSHIP_TRANSFER_REVERTED = 'MEMBERSHIP_TRANSFER_REVERTED',
  MEMBER_MOVEMENT_CONFIG_UPDATED = 'MEMBER_MOVEMENT_CONFIG_UPDATED',
  MEMBER_MOVEMENT_RUN_APPROVED = 'MEMBER_MOVEMENT_RUN_APPROVED',
  MEMBER_MOVEMENT_ROLLING_TOGGLED = 'MEMBER_MOVEMENT_ROLLING_TOGGLED',
  MEMBER_MOVEMENT_EMERGENCY_STOP = 'MEMBER_MOVEMENT_EMERGENCY_STOP',
  MEMBER_MOVEMENT_LOCATION_POSTURE_UPDATED = 'MEMBER_MOVEMENT_LOCATION_POSTURE_UPDATED',
  MEMBER_MOVEMENT_INCOMING_NOTIFIERS_UPDATED = 'MEMBER_MOVEMENT_INCOMING_NOTIFIERS_UPDATED',
  MEMBER_MOVEMENT_DESTINATION_CHOSEN = 'MEMBER_MOVEMENT_DESTINATION_CHOSEN',
  MEMBER_MOVEMENT_REINITIATED = 'MEMBER_MOVEMENT_REINITIATED',
  MEMBER_MOVEMENT_INCOMING_DISMISSED = 'MEMBER_MOVEMENT_INCOMING_DISMISSED',

  // Tanning Events (OC-1372)
  TANNING_SESSION_REVERSED = 'TANNING_SESSION_REVERSED',
  TANNING_SKIN_TYPE_OVERRIDE = 'TANNING_SKIN_TYPE_OVERRIDE',
  TANNING_REGULATION_OVERRIDE = 'TANNING_REGULATION_OVERRIDE',
  OWED_BALANCE_BLOCK_OVERRIDE = 'OWED_BALANCE_BLOCK_OVERRIDE',
  FORM_RESULT_RECORDED_EXTERNALLY = 'FORM_RESULT_RECORDED_EXTERNALLY',

  // Tanning lamp hour tracking (OC-1698)
  EQUIPMENT_LAMP_SETTINGS_UPDATED = 'EQUIPMENT_LAMP_SETTINGS_UPDATED',
  EQUIPMENT_LAMP_CHANGED = 'EQUIPMENT_LAMP_CHANGED',
  EQUIPMENT_LAMP_HOURS_ADJUSTED = 'EQUIPMENT_LAMP_HOURS_ADJUSTED',

  // Form evidence retention (INT-1224)
  FORM_RECORDS_PURGED = 'FORM_RECORDS_PURGED',
  // Sales commissions (OC-1721)
  COMMISSION_PLAN_SAVED = 'COMMISSION_PLAN_SAVED',
  COMMISSION_PLAN_AVAILABILITY_CHANGED = 'COMMISSION_PLAN_AVAILABILITY_CHANGED',
  COMMISSION_ASSIGNMENT_CHANGED = 'COMMISSION_ASSIGNMENT_CHANGED',
  COMMISSION_ELIGIBILITY_CHANGED = 'COMMISSION_ELIGIBILITY_CHANGED',
  COMMISSION_SETTINGS_CHANGED = 'COMMISSION_SETTINGS_CHANGED',
  COMMISSION_ITEM_SETTING_CHANGED = 'COMMISSION_ITEM_SETTING_CHANGED',
  COMMISSION_OWNER_CHANGED = 'COMMISSION_OWNER_CHANGED',
  COMMISSION_ADJUSTMENT_ADDED = 'COMMISSION_ADJUSTMENT_ADDED',
  COMMISSION_PERIOD_TRANSITIONED = 'COMMISSION_PERIOD_TRANSITIONED',
  COMMISSION_ACTIVATION_STEP = 'COMMISSION_ACTIVATION_STEP',
}

export enum AuditResource {
  MEMBER = 'MEMBER',
  SESSION = 'SESSION',
  BOOKING = 'BOOKING',
  PAYMENT = 'PAYMENT',
  ACCOUNT_CODE = 'ACCOUNT_CODE', // CORE-1322 / INT-67: per-site accounting-code library (neutral; DD-011)
  CASH_DRAWER = 'CASH_DRAWER', // OC-128: cash drawer sessions (register float custody)
  INVOICE = 'INVOICE',
  MEMBERSHIP = 'MEMBERSHIP',
  SERVICE = 'SERVICE', // OC-8: a client's purchased Service instance (package share lifecycle)
  FAMILY = 'FAMILY',
  STAFF = 'STAFF',
  ALLOWED_NETWORK = 'ALLOWED_NETWORK',
  ADMIN_USER = 'ADMIN_USER',
  CORPORATE = 'CORPORATE',
  SITE = 'SITE',
  SITE_ALERT = 'SITE_ALERT',
  DOOR = 'DOOR',
  ACCESS_POINT = 'ACCESS_POINT',
  ACCESS_ZONE = 'ACCESS_ZONE',
  SYSTEM_CONFIG = 'SYSTEM_CONFIG',
  PAGE = 'PAGE',
  REPORT = 'REPORT',
  SETTING = 'SETTING',
  EQUIPMENT = 'EQUIPMENT', // OC-1698: a tanning bed / wellness equipment unit

  // Knowledge Hub Resources
  KNOWLEDGE_ARTICLE = 'KNOWLEDGE_ARTICLE',
  KNOWLEDGE_CATEGORY = 'KNOWLEDGE_CATEGORY',
  KNOWLEDGE_TRANSLATION = 'KNOWLEDGE_TRANSLATION',

  // AI Assistant Resources
  AI_CONVERSATION = 'AI_CONVERSATION',
  AI_INTERACTION = 'AI_INTERACTION',
  SUPPORT_TICKET = 'SUPPORT_TICKET',

  // Integration Resources
  INTEGRATION_SYNC = 'INTEGRATION_SYNC',
  INTEGRATION_CLIENT = 'INTEGRATION_CLIENT',
  INTEGRATION_LOCATION = 'INTEGRATION_LOCATION',
  INTEGRATION_TOKEN = 'INTEGRATION_TOKEN',
  // Provider-specific resource so HubSpot events are filterable (mirrors GROW_*).
  HUBSPOT = 'HUBSPOT',
  // Provider-specific resource so eGym sync events are filterable in the marketplace History tab.
  EGYM = 'EGYM',

  // Grow Resources
  GROW_CORPORATE = 'GROW_CORPORATE',
  GROW_HQ = 'GROW_HQ',
  GROW_SITE = 'GROW_SITE',
  GROW_TOKEN = 'GROW_TOKEN',

  // Content Suite Resources
  CONTENT_ENTRY = 'CONTENT_ENTRY',
  // A content type (taxonomy/article/video/...), not an individual entry. Reorder
  // is scoped to the type, so its audit row references the type rather than
  // pointing resourceId at an entry that was never touched (MEM-413).
  CONTENT_TYPE = 'CONTENT_TYPE',
  CONTENT_VIDEO = 'CONTENT_VIDEO',
  CONTENT_CHALLENGE = 'CONTENT_CHALLENGE',
  CONTENT_METRIC = 'CONTENT_METRIC',
  CONTENT_AWARD = 'CONTENT_AWARD',
  CONTENT_LEADERBOARD = 'CONTENT_LEADERBOARD',
  CONTENT_CAROUSEL_GROUP = 'CONTENT_CAROUSEL_GROUP',
  CONTENT_GOAL = 'CONTENT_GOAL',
  SHARE_CARD = 'SHARE_CARD',

  // Zapier Resources
  ZAPIER_CONNECTION = 'ZAPIER_CONNECTION',
  FITNESS_PASSPORT_LINK = 'FITNESS_PASSPORT_LINK',
  FITNESS_PASSPORT_INTEGRATION = 'FITNESS_PASSPORT_INTEGRATION',
  ZAPIER_SUBSCRIPTION = 'ZAPIER_SUBSCRIPTION',

  // Webhook Resources
  WEBHOOK = 'WEBHOOK',
  INTEGRATION_WEBHOOK_DELIVERY = 'INTEGRATION_WEBHOOK_DELIVERY',

  // Public-API Resources (INT-705)
  API_CREDENTIAL = 'API_CREDENTIAL',

  // Additional resources found in data
  COMMUNICATION = 'COMMUNICATION',

  // Dispute Resources
  DISPUTE = 'DISPUTE',

  // Demo Resources
  DEMO_PROFILE = 'DEMO_PROFILE',

  // Import Resources
  IMPORT_SESSION = 'IMPORT_SESSION',

  // Trial Resources
  TRIAL = 'TRIAL',

  // Invitation Resources
  STAFF_INVITATION = 'STAFF_INVITATION',

  // Onboarding Resources
  ONBOARDING_CHECKLIST = 'ONBOARDING_CHECKLIST',

  // Agreement Resources
  AGREEMENT = 'AGREEMENT',
  SITE_POLICY = 'SITE_POLICY',
  EQUIPMENT_JURISDICTION_RULE = 'EQUIPMENT_JURISDICTION_RULE',

  // Tanning Resources
  EQUIPMENT_ASSIGNMENT = 'EQUIPMENT_ASSIGNMENT',
  CLIENT_FORM_SUBMISSION = 'CLIENT_FORM_SUBMISSION',
  // Sales commissions (OC-1721)
  COMMISSION = 'COMMISSION',
}

export enum AuditVisibility {
  CUSTOMER = 'CUSTOMER', // Visible to customers in their audit trail
  INTERNAL = 'INTERNAL', // Only visible to staff/admin
  SYSTEM = 'SYSTEM', // System-level events (debugging)
}

export enum AuditSeverity {
  INFO = 'INFO',
  WARNING = 'WARNING',
  CRITICAL = 'CRITICAL',
  SECURITY = 'SECURITY',
}

export enum AuditSource {
  CORE_WEB = 'CORE_WEB',
  ADMIN_WEB = 'ADMIN_WEB',
  EMBED_WEB = 'EMBED_WEB',
  MOBILE = 'MOBILE',
  KIOSK = 'KIOSK',
  API = 'API',
  SYSTEM = 'SYSTEM',
  WEBHOOK = 'WEBHOOK',
  INTEGRATION = 'INTEGRATION', // Integration sync operations (Grow, Mindbody, etc.)
}

export enum AuditPlatform {
  WEB = 'WEB',
  IOS = 'IOS',
  ANDROID = 'ANDROID',
}

export enum AuditDeviceType {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

// =============================================================================
// Zod Enum Schemas (for GraphQL)
// =============================================================================

export const auditActionSchema = z.nativeEnum(AuditAction).register(asEnumType, {
  name: 'AuditAction',
  description: 'Type of audit action',
})

export const auditResourceSchema = z.nativeEnum(AuditResource).register(asEnumType, {
  name: 'AuditResource',
  description: 'Type of resource being audited',
})

export const auditVisibilitySchema = z.nativeEnum(AuditVisibility).register(asEnumType, {
  name: 'AuditVisibility',
  description: 'Visibility level of the audit event',
})

export const auditSeveritySchema = z.nativeEnum(AuditSeverity).register(asEnumType, {
  name: 'AuditSeverity',
  description: 'Severity level of the audit event',
})

export const auditSourceSchema = z.nativeEnum(AuditSource).register(asEnumType, {
  name: 'AuditSource',
  description: 'Source application of the audit event',
})

export const auditPlatformSchema = z.nativeEnum(AuditPlatform).register(asEnumType, {
  name: 'AuditPlatform',
  description: 'Platform/OS of the client device',
})

export const auditDeviceTypeSchema = z.nativeEnum(AuditDeviceType).register(asEnumType, {
  name: 'AuditDeviceType',
  description: 'Device type (mobile, tablet, desktop)',
})

// =============================================================================
// Field Change Schema (for tracking from/to states)
// =============================================================================

export const fieldChangeSchema = z
  .object({
    field: z.string().describe('Field name that changed'),
    oldValue: z.string().nullable().optional().describe('Previous value (stringified)'),
    newValue: z.string().nullable().optional().describe('New value (stringified)'),
    fieldType: z.string().optional().describe('Type hint (string, number, boolean, etc.)'),
    displayName: z.string().optional().describe('Human-readable field name'),
  })
  .register(asObjectType, { name: 'AuditFieldChange' })

// =============================================================================
// Details Schema (flexible JSON for event-specific data)
// Note: Complex JSON fields (previousValue, newValue, metadata, context) are stored as serialized JSON strings
// because GraphQL doesn't support arbitrary JSON without a custom scalar.
// =============================================================================

export const auditEventDetailsSchema = z
  .object({
    // Full object snapshots for comprehensive diff (stored as JSON strings)
    previousValueJson: z
      .string()
      .optional()
      .describe('Complete object state before change (JSON string)'),
    newValueJson: z
      .string()
      .optional()
      .describe('Complete object state after change (JSON string)'),

    // Field-level changes for UI display
    changes: z.array(fieldChangeSchema).optional().describe('List of individual field changes'),

    // Additional context (stored as JSON strings)
    metadataJson: z
      .string()
      .optional()
      .describe('Additional event-specific metadata (JSON string)'),
    reason: z.string().optional().describe('Reason for the action (if applicable)'),
    contextJson: z.string().optional().describe('Additional context information (JSON string)'),

    // For UI display
    summary: z.string().optional().describe('Human-readable summary of changes'),
  })
  .register(asObjectType, { name: 'AuditEventDetails' })

// =============================================================================
// Main Audit Event Schema
// =============================================================================

export const auditEventSchema = z
  .object({
    id: z.string().uuid(),
    schemaVersion: z.number().int().default(CURRENT_SCHEMA_VERSION),
    corporateId: z.string(),
    corporateSlug: z.string().nullable().optional(),
    corporateName: z.string().nullable().optional().describe('Corporate/brand name (denormalized)'),
    siteId: z.string().nullable().optional(),
    siteName: z.string().nullable().optional().describe('Site/location name (denormalized)'),
    userId: z.string().nullable().optional().describe('Staff/system user who performed action'),
    userEmail: z
      .string()
      .nullable()
      .optional()
      .describe('Email of user who performed action (denormalized)'),
    userFirstName: z
      .string()
      .nullable()
      .optional()
      .describe('First name of user who performed action (denormalized)'),
    userLastName: z
      .string()
      .nullable()
      .optional()
      .describe('Last name of user who performed action (denormalized)'),
    memberId: z.string().nullable().optional().describe('Member/customer affected'),
    affectedUserId: z
      .string()
      .nullable()
      .optional()
      .describe('Staff user who was affected by the action (e.g. user being edited/deleted)'),
    affectedUserEmail: z
      .string()
      .nullable()
      .optional()
      .describe('Email of affected user (denormalized)'),
    affectedUserFirstName: z
      .string()
      .nullable()
      .optional()
      .describe('First name of affected user (denormalized)'),
    affectedUserLastName: z
      .string()
      .nullable()
      .optional()
      .describe('Last name of affected user (denormalized)'),
    action: auditActionSchema,
    resource: auditResourceSchema,
    resourceId: z.string().nullable().optional(),
    timestamp: z.coerce.date(),
    ipAddress: z.string().nullable().optional(),
    userAgent: z.string().nullable().optional(),
    // User context (non-PII)
    emailDomain: z
      .string()
      .nullable()
      .optional()
      .describe('Email domain without username (e.g., "hapana.com")'),
    userRoles: z
      .array(z.string())
      .nullable()
      .optional()
      .describe('User roles/permissions at time of action'),
    // Device context (parsed from userAgent for easier querying)
    deviceType: auditDeviceTypeSchema
      .nullable()
      .optional()
      .describe('Device type (mobile, tablet, desktop)'),
    browserName: z
      .string()
      .nullable()
      .optional()
      .describe('Browser name (Chrome, Firefox, Safari, etc.)'),
    browserVersion: z.string().nullable().optional().describe('Browser version'),
    osName: z
      .string()
      .nullable()
      .optional()
      .describe('Operating system name (Windows, macOS, iOS, Android, etc.)'),
    osVersion: z.string().nullable().optional().describe('Operating system version'),
    screenWidth: z.number().int().nullable().optional().describe('Screen width in pixels'),
    screenHeight: z.number().int().nullable().optional().describe('Screen height in pixels'),
    language: z.string().nullable().optional().describe('Browser language (e.g., "en-US")'),
    timezone: z.string().nullable().optional().describe('User timezone (e.g., "Australia/Sydney")'),
    // Geographic context (from server-side IP lookup - no user permission required)
    geoCity: z.string().nullable().optional().describe('City from IP lookup (e.g., "Sydney")'),
    geoCountry: z
      .string()
      .nullable()
      .optional()
      .describe('Country code from IP lookup (e.g., "AU")'),
    geoCountryName: z
      .string()
      .nullable()
      .optional()
      .describe('Country name from IP lookup (e.g., "Australia")'),
    geoRegion: z
      .string()
      .nullable()
      .optional()
      .describe('Region/state from IP lookup (e.g., "NSW")'),
    geoLatitude: z.number().nullable().optional().describe('Latitude from IP lookup'),
    geoLongitude: z.number().nullable().optional().describe('Longitude from IP lookup'),
    geoTimezone: z
      .string()
      .nullable()
      .optional()
      .describe('Timezone from IP lookup (e.g., "Australia/Sydney")'),
    geoIsEU: z.boolean().nullable().optional().describe('Whether IP is in EU (for GDPR)'),
    details: auditEventDetailsSchema.nullable().optional(),
    visibility: auditVisibilitySchema,
    severity: auditSeveritySchema,
    source: auditSourceSchema,
    platform: auditPlatformSchema.nullable().optional().describe('Platform/OS (iOS, Android, Web)'),
    sessionId: z.string().nullable().optional(),
    requestId: z.string().nullable().optional(),
    // Page visit specific
    pageUrl: z.string().nullable().optional(),
    pageName: z.string().nullable().optional(),
    // Door specific
    doorId: z.string().nullable().optional(),
    accessPointId: z.string().nullable().optional(),
  })
  .register(asObjectType, { name: 'AuditEvent' })

// =============================================================================
// Input Schemas
// =============================================================================

export const createAuditEventInputSchema = z.object({
  corporateId: z.string(),
  corporateSlug: z.string().optional().describe('Corporate slug for dataset naming'),
  corporateName: z.string().optional().describe('Corporate/brand name (denormalized)'),
  siteId: z.string().nullable().optional(),
  siteName: z.string().nullable().optional(),
  userId: z.string().nullable().optional(),
  userEmail: z.string().nullable().optional(),
  userFirstName: z.string().nullable().optional(),
  userLastName: z.string().nullable().optional(),
  memberId: z.string().nullable().optional(),
  affectedUserId: z.string().nullable().optional(),
  affectedUserEmail: z.string().nullable().optional(),
  affectedUserFirstName: z.string().nullable().optional(),
  affectedUserLastName: z.string().nullable().optional(),
  action: auditActionSchema,
  resource: auditResourceSchema,
  resourceId: z.string().nullable().optional(),
  ipAddress: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
  // User context (non-PII)
  emailDomain: z.string().nullable().optional().describe('Email domain without username'),
  userRoles: z.array(z.string()).nullable().optional().describe('User roles at time of action'),
  // Device context
  deviceType: auditDeviceTypeSchema.nullable().optional(),
  browserName: z.string().nullable().optional(),
  browserVersion: z.string().nullable().optional(),
  osName: z.string().nullable().optional(),
  osVersion: z.string().nullable().optional(),
  screenWidth: z.number().int().nullable().optional(),
  screenHeight: z.number().int().nullable().optional(),
  language: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  details: z
    .object({
      previousValueJson: z.string().optional(),
      newValueJson: z.string().optional(),
      changes: z
        .array(
          z.object({
            field: z.string(),
            oldValue: z.string().nullable().optional(),
            newValue: z.string().nullable().optional(),
            fieldType: z.string().optional(),
            displayName: z.string().optional(),
          })
        )
        .optional(),
      metadataJson: z.string().optional(),
      reason: z.string().optional(),
      contextJson: z.string().optional(),
      summary: z.string().optional(),
    })
    .optional(),
  visibility: auditVisibilitySchema.default(AuditVisibility.INTERNAL),
  severity: auditSeveritySchema.default(AuditSeverity.INFO),
  source: auditSourceSchema,
  platform: auditPlatformSchema.nullable().optional(),
  sessionId: z.string().nullable().optional(),
  pageUrl: z.string().nullable().optional(),
  pageName: z.string().nullable().optional(),
  doorId: z.string().nullable().optional(),
  accessPointId: z.string().nullable().optional(),
})

// =============================================================================
// Query Schemas
// =============================================================================

export const auditQueryFilterSchema = z.object({
  corporateId: z.string(),
  siteId: z.string().optional(),
  userId: z.string().optional(),
  memberId: z.string().optional(),
  affectedUserId: z.string().optional(),
  actions: z.array(auditActionSchema).optional(),
  excludeActions: z.array(auditActionSchema).optional(),
  resources: z.array(auditResourceSchema).optional(),
  visibility: z.array(auditVisibilitySchema).optional(),
  severity: z.array(auditSeveritySchema).optional(),
  source: z.array(auditSourceSchema).optional(),
  searchText: z.string().optional(),
  systemOnly: z.boolean().optional(),
  dateRange: z
    .object({
      startDate: z.coerce.date(),
      endDate: z.coerce.date(),
    })
    .optional(),
})

export const auditSortFieldSchema = z
  .enum(['timestamp', 'action', 'resource', 'severity'])
  .register(asEnumType, { name: 'AuditSortField', description: 'Field to sort audit events by' })

export const sortDirectionSchema = z.enum(['asc', 'desc']).register(asEnumType, {
  name: 'AuditSortDirection',
  description: 'Sort direction for audit queries',
})

export const auditQuerySortSchema = z.object({
  field: auditSortFieldSchema.default('timestamp'),
  direction: sortDirectionSchema.default('desc'),
})

export const auditPaginationSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(25),
})

export const auditQueryInputSchema = z.object({
  filter: auditQueryFilterSchema,
  sort: auditQuerySortSchema.optional(),
  pagination: auditPaginationSchema.optional(),
})

// Admin query filter - corporateId is optional for cross-corporate queries
export const adminAuditQueryFilterSchema = z.object({
  corporateId: z.string().optional(),
  siteId: z.string().optional(),
  userId: z.string().optional(),
  memberId: z.string().optional(),
  affectedUserId: z.string().optional(),
  actions: z.array(auditActionSchema).optional(),
  resources: z.array(auditResourceSchema).optional(),
  visibility: z.array(auditVisibilitySchema).optional(),
  severity: z.array(auditSeveritySchema).optional(),
  source: z.array(auditSourceSchema).optional(),
  searchText: z.string().optional(),
  systemOnly: z.boolean().optional(),
  dateRange: z
    .object({
      startDate: z.coerce.date(),
      endDate: z.coerce.date(),
    })
    .optional(),
})

export const adminAuditQueryInputSchema = z.object({
  filter: adminAuditQueryFilterSchema.optional(),
  sort: auditQuerySortSchema.optional(),
  pagination: auditPaginationSchema.optional(),
})

export const auditPaginationResultSchema = z
  .object({
    currentPage: z.number(),
    pageSize: z.number(),
    totalPages: z.number(),
    totalCount: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
  })
  .register(asObjectType, { name: 'AuditPaginationResult' })

export const auditQueryResultSchema = z
  .object({
    events: z.array(auditEventSchema),
    pagination: auditPaginationResultSchema,
  })
  .register(asObjectType, { name: 'AuditQueryResult' })

// =============================================================================
// Aggregation Schemas
// =============================================================================

export const auditAggregationGroupBySchema = z
  .enum([
    'action',
    'resource',
    'userId',
    'affectedUserId',
    'siteName',
    'severity',
    'source',
    'date',
  ])
  .register(asEnumType, {
    name: 'AuditAggregationGroupBy',
    description: 'Field to group audit events by for aggregation',
  })

export const auditAggregationGroupSchema = z
  .object({
    groupKey: z.string().describe('Raw value of the grouped field'),
    groupLabel: z.string().describe('Human-readable label for the group'),
    count: z.number().int().describe('Number of events in this group'),
  })
  .register(asObjectType, { name: 'AuditAggregationGroup' })

export const auditAggregationResultSchema = z
  .object({
    groups: z.array(auditAggregationGroupSchema),
    totalCount: z.number().int().describe('Total matching events across all groups'),
    groupBy: z.string().describe('The field that was grouped by'),
  })
  .register(asObjectType, { name: 'AuditAggregationResult' })

export const auditAggregationInputSchema = z.object({
  filter: auditQueryFilterSchema,
  groupBy: auditAggregationGroupBySchema,
  topN: z.number().int().min(1).max(100).default(25),
  sortDirection: sortDirectionSchema.default('desc'),
})

// =============================================================================
// Type Exports
// =============================================================================

export type AuditEvent = z.infer<typeof auditEventSchema>
export type CreateAuditEventInput = z.infer<typeof createAuditEventInputSchema>
export type AuditQueryFilter = z.infer<typeof auditQueryFilterSchema>
export type AuditQueryInput = z.infer<typeof auditQueryInputSchema>
export type AdminAuditQueryFilter = z.infer<typeof adminAuditQueryFilterSchema>
export type AdminAuditQueryInput = z.infer<typeof adminAuditQueryInputSchema>
export type AuditQueryResult = z.infer<typeof auditQueryResultSchema>
export type AuditAggregationResult = z.infer<typeof auditAggregationResultSchema>
export type AuditAggregationInput = z.infer<typeof auditAggregationInputSchema>
export type AuditAggregationGroup = z.infer<typeof auditAggregationGroupSchema>
export type AuditEventDetails = z.infer<typeof auditEventDetailsSchema>
export type FieldChange = z.infer<typeof fieldChangeSchema>
